import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getClubMarks, getClubEvents, getEventBundle, loadWip, getSessionTrack } from '../lib/firebase.js';
import { loadFleetConfig } from '../lib/fleetConfigStore.js';
import { canonicalizeMarkIds } from '../lib/coursePreviewLayout.js';
import { courseMatchesDirection } from '../lib/courseConstants.js';
import { normalizeMarks } from '../lib/markNormalize.js';
import { parseTrackData } from '../lib/trackOverlay.js';
import CourseMap from '../components/CourseMap.jsx';
import SessionPicker from '../components/SessionPicker.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import CourseFilterBar from '../components/CourseFilterBar.jsx';
import CourseListPanel from '../components/CourseListPanel.jsx';
import PortalHeader from '../components/PortalHeader';

/**
 * Flatten a published bundle's nested course_cards structure into a flat
 * array of course objects, each annotated with its parent card name/id.
 *
 * Published bundles have: course_cards[].courses[] (nested under starts)
 * Draft bundles have: course_cards[] as a flat array (each item IS a course)
 */
function flattenBundleCourses(bundle) {
  if (!bundle || !bundle.course_cards) return [];

  const courses = [];
  for (const card of bundle.course_cards) {
    if (Array.isArray(card.courses)) {
      for (const course of card.courses) {
        courses.push({
          ...course,
          id: course.id || crypto.randomUUID(),
          cardName: card.name || 'Unnamed',
          cardId: card.id || card.name || 'unknown',
        });
      }
    } else {
      // Draft-style flat structure: the card itself IS a course
      courses.push({
        ...card,
        id: card.id || crypto.randomUUID(),
        cardName: null,
        cardId: null,
      });
    }
  }
  return courses;
}

/** Format a track's ISO start/end into a "HH:MM:SS–HH:MM:SS" span. */
function formatTrackSpan(start, end) {
  const fmt = (ts) => {
    const d = new Date(ts + (ts.includes('+') || ts.includes('Z') ? '' : 'Z'));
    return Number.isNaN(d.getTime()) ? ts : d.toISOString().slice(11, 19);
  };
  return `${fmt(start)}–${fmt(end)}`;
}

/** Format event_id like "2025-summer" into "Summer 2025". */
function formatEventLabel(eventId, eventName) {
  if (eventName) return eventName;
  const parts = eventId.split('-');
  if (parts.length === 2) {
    const [year, season] = parts;
    return `${season.charAt(0).toUpperCase() + season.slice(1)} ${year}`;
  }
  return eventId;
}

export default function CourseViewer() {
  const { clubId } = useParams();
  // The recorded-session browse is backed by admin-only callables
  // (listHistoricalSessions / getSessionTrack), and the viewer route is open to
  // ClubAdmin/RaceOfficer too — so only surface the browse control to admins to
  // keep the UI aligned with the backend (and match Live Activity's gating). The
  // file fallback parses locally and stays available to everyone. (codex c476ac3.)
  const { isGlobalAdmin } = useAuth();
  const [marks, setMarks] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [bundle, setBundle] = useState(null);
  const [fleetMap, setFleetMap] = useState({});
  const [fleets, setFleets] = useState([]);
  const [startLocations, setStartLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ direction: '', fleet: '', startLocation: '', cardName: '' });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [focusedId, setFocusedId] = useState(null);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [startNm, setStartNm] = useState(0.5);
  const [finishNm, setFinishNm] = useState(0.25);
  const [viewMode, setViewMode] = useState('published'); // 'published' | 'draft'
  const [track, setTrack] = useState([]);
  const [trackInfo, setTrackInfo] = useState(null); // { name, count, start, end, courseName? }
  const [trackError, setTrackError] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  // Load events list + marks + fleet config on mount
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [clubMarks, clubEvents, config] = await Promise.all([
          getClubMarks(clubId),
          getClubEvents(clubId),
          loadFleetConfig(clubId),
        ]);
        if (cancelled) return;
        setMarks(clubMarks);
        // Filter to available events, sort by most recent first
        const available = clubEvents
          .filter((e) => e.status === 'available' || e.status === 'coming_soon')
          .sort((a, b) => (b.content_version ?? 0) - (a.content_version ?? 0));
        setEvents(available);
        setFleets(config.fleets || []);
        setStartLocations(config.start_locations || []);

        // Default to first available event
        if (available.length > 0) {
          setSelectedEventId(available[0].id);
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load data: ' + err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [clubId]);

  // Load bundle when selected event or view mode changes
  useEffect(() => {
    if (!selectedEventId) {
      setBundle(null);
      return;
    }
    const event = events.find((e) => e.id === selectedEventId);
    if (!event && viewMode === 'published') {
      setBundle(null);
      return;
    }

    let cancelled = false;
    async function loadBundleData() {
      setBundleLoading(true);
      setError('');
      try {
        let b = null;
        if (viewMode === 'draft') {
          b = await loadWip(clubId, selectedEventId);
        } else {
          const version = event?.content_version ?? 1;
          b = await getEventBundle(clubId, selectedEventId, version);
        }
        if (!cancelled) setBundle(b);
      } catch (err) {
        if (!cancelled) setError('Failed to load bundle: ' + err.message);
      } finally {
        if (!cancelled) setBundleLoading(false);
      }
    }
    loadBundleData();
    return () => { cancelled = true; };
  }, [clubId, selectedEventId, events, viewMode]);

  // Flattened courses from the selected bundle
  // Use bundle-embedded marks if available, else fall back to club marks.
  // Normalize mark SHAPE here (mark_id→id, lat/lng/nested→latitude/longitude,
  // string coords→number) — the same normalizeMarks the committee applies in
  // mergeMarks — so a published bundle in a non-canonical shape resolves in the
  // viewer's exact id/latitude/longitude lookups AND in canonicalizeMarkIds
  // below (which matches on m.id). (codex review on e7edaa0c.)
  const effectiveMarks = useMemo(() => {
    if (bundle?.marks && bundle.marks.length > 0) return normalizeMarks(bundle.marks);
    return normalizeMarks(marks);
  }, [bundle, marks]);

  // Canonicalize each course's markIds to the marks' real case at this single
  // load point, so every downstream viewer consumer (CourseListPanel, CourseMap,
  // filters) resolves the marks. Already-published bundles corrupted by the old
  // lowercasing bug (ra-or1y: ['n','m','c'] vs uppercase marks) otherwise render
  // blank legs / missing positions in the viewer. (codex review on 43f7def6.)
  const courses = useMemo(
    () => flattenBundleCourses(bundle).map((c) => ({
      ...c,
      markIds: canonicalizeMarkIds(c.markIds, effectiveMarks),
    })),
    [bundle, effectiveMarks],
  );

  // Build fleet map from selected bundle
  useEffect(() => {
    if (!bundle) {
      setFleetMap({});
      return;
    }
    const fm = {};
    (bundle.fleet_assignments || []).forEach((fa) => {
      fm[fa.course_card_id] = fa.fleet_id;
    });
    setFleetMap(fm);
  }, [bundle]);

  // Unique card names for filter dropdown
  const cardNames = useMemo(() => {
    const names = new Set();
    for (const c of courses) {
      if (c.cardName) names.add(c.cardName);
    }
    return Array.from(names).sort();
  }, [courses]);

  // Apply filters
  const filteredCourses = useMemo(() => {
    return courses.filter((course) => {
      // Grid courses match the Wind filter on directionCode; named courses
      // (directionCode == null) match via their wind sector. The old
      // directionCode-only check silently dropped every named course whenever a
      // Wind direction was picked. (ra-gbrd.35)
      if (filters.direction && !courseMatchesDirection(course, filters.direction)) return false;
      if (filters.fleet && fleetMap[course.id] !== filters.fleet) return false;
      if (filters.cardName && course.cardName !== filters.cardName) return false;
      return true;
    });
  }, [courses, filters, fleetMap]);

  // Selected courses (intersection with filtered)
  const selectedCourses = useMemo(() => {
    return filteredCourses.filter((c) => selectedIds.has(c.id));
  }, [filteredCourses, selectedIds]);

  const handleToggle = useCallback((courseId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(courseId)) {
        next.delete(courseId);
      } else {
        next.add(courseId);
      }
      return next;
    });
  }, []);

  const handleFocus = useCallback((courseId) => {
    setFocusedId((prev) => (prev === courseId ? null : courseId));
  }, []);

  // Overlay a recorded session's track. The events/ tree is read server-side
  // (getSessionTrack), which returns a data.json-shaped { track, inst } we parse
  // through the same path as a loaded file. The session's recorded course is
  // surfaced to help the admin IDENTIFY which candidate course was sailed
  // (ra-97jo). Throws so SessionPicker can surface failures inline.
  const handleSessionSelect = useCallback(async (session) => {
    const data = await getSessionTrack(session.sessionPath);
    const parsed = parseTrackData({ track: data.track, inst: data.inst });
    if (parsed.points.length === 0) {
      throw new Error('That session has no usable GPS track.');
    }
    setTrackError('');
    setTrack(parsed.points);
    const label = `${session.vesselId || session.vessel || 'Session'} ${session.date || ''}`.trim();
    setTrackInfo({
      name: label,
      ...parsed.summary,
      courseName: data.meta?.courseName || session.courseName || null,
    });
    setPickerOpen(false);
  }, []);

  // Fallback: load a boat GPS track from a local analysis data.json (or a bare
  // track array). The recorded-session picker is the primary path; a file is
  // the exception (Chris: "the file is the exception, not the rule").
  const handleTrackFile = useCallback((e) => {
    const file = e.target.files && e.target.files[0];
    // Allow re-loading the same file by resetting the input value.
    e.target.value = '';
    if (!file) return;
    setTrackError('');
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseTrackData(JSON.parse(reader.result));
        if (parsed.points.length === 0) {
          setTrackError('No track points found in that file.');
          return;
        }
        setTrack(parsed.points);
        setTrackInfo({ name: file.name, ...parsed.summary });
      } catch {
        setTrackError('Could not parse file as JSON.');
      }
    };
    reader.onerror = () => setTrackError('Could not read file.');
    reader.readAsText(file);
  }, []);

  const handleClearTrack = useCallback(() => {
    setTrack([]);
    setTrackInfo(null);
    setTrackError('');
  }, []);

  const handleToggleSection = useCallback((cardName, courseIds, allSelected) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        courseIds.forEach((id) => next.delete(id));
      } else {
        courseIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  // Clear selection when season changes
  useEffect(() => {
    setSelectedIds(new Set());
    setFocusedId(null);
    setFilters((f) => ({ ...f, cardName: '' }));
    setViewMode('published');
  }, [selectedEventId]);

  if (loading) {
    return <div className="page"><p>Loading course data...</p></div>;
  }

  return (
    <>
      <PortalHeader />
      <div className="viewer-layout">
        <header className="viewer-header">
          <Link to={`/clubs/${clubId}`} className="back-link">&larr; Dashboard</Link>
          <h1>Course Viewer</h1>
          {viewMode === 'draft' && <span className="draft-badge">Draft</span>}

          {events.length > 0 && (
            <>
              <label className="season-selector">
                <span className="filter-label">Season</span>
                <select
                  value={selectedEventId}
                  onChange={(e) => setSelectedEventId(e.target.value)}
                >
                  {events.map((ev) => (
                    <option key={ev.id} value={ev.id}>
                      {formatEventLabel(ev.id, ev.name)}
                    </option>
                  ))}
                </select>
              </label>
              <div className="viewer-mode-toggle">
                <button
                  className={viewMode === 'published' ? 'active' : ''}
                  onClick={() => setViewMode('published')}
                >
                  Published
                </button>
                <button
                  className={viewMode === 'draft' ? 'active' : ''}
                  onClick={() => setViewMode('draft')}
                >
                  Draft
                </button>
              </div>
            </>
          )}

          {error && <span className="error-text">{error}</span>}

          <div className="track-loader">
            <span className="filter-label">Track</span>
            {isGlobalAdmin && (
              <button
                type="button"
                className="track-browse-btn"
                onClick={() => setPickerOpen(true)}
              >
                Browse recorded sessions
              </button>
            )}
            <label
              className="track-file-fallback"
              title="Advanced: overlay a track from an analysis data.json file"
            >
              <span className="filter-label">{isGlobalAdmin ? 'or file' : 'from file'}</span>
              <input
                type="file"
                accept=".json,application/json"
                onChange={handleTrackFile}
                aria-label="Load boat GPS track from file"
              />
            </label>
            {trackInfo && (
              <>
                <span className="track-summary">
                  {trackInfo.name}: {trackInfo.count} pts
                  {trackInfo.start && trackInfo.end && (
                    <> ({formatTrackSpan(trackInfo.start, trackInfo.end)})</>
                  )}
                  {trackInfo.courseName && (
                    <> · recorded course <strong>{trackInfo.courseName}</strong></>
                  )}
                </span>
                <button type="button" onClick={handleClearTrack}>Clear track</button>
              </>
            )}
            {trackError && <span className="track-error">{trackError}</span>}
          </div>
        </header>

        {isGlobalAdmin && pickerOpen && (
          <SessionPicker
            onSelect={handleSessionSelect}
            onClose={() => setPickerOpen(false)}
          />
        )}

      {events.length === 0 ? (
        <div className="page">
          <p className="empty-state">No published courses for this club.</p>
        </div>
      ) : bundleLoading ? (
        <div className="page"><p>Loading courses...</p></div>
      ) : !bundle && viewMode === 'draft' ? (
        <div className="page">
          <p className="empty-state">No draft in progress for this season.</p>
        </div>
      ) : (
        <div className="viewer-body">
          <div className={`viewer-sidebar ${panelCollapsed ? 'collapsed' : ''}`}>
            <CourseFilterBar
              filters={filters}
              onChange={setFilters}
              fleets={fleets}
              startLocations={startLocations}
              cardNames={cardNames}
              startNm={startNm}
              finishNm={finishNm}
              onStartNmChange={setStartNm}
              onFinishNmChange={setFinishNm}
            />
            <CourseListPanel
              cards={filteredCourses}
              selectedIds={selectedIds}
              onToggle={handleToggle}
              onToggleSection={handleToggleSection}
              focusedId={focusedId}
              onFocus={handleFocus}
              fleetMap={fleetMap}
              marks={effectiveMarks}
              collapsed={panelCollapsed}
              onToggleCollapse={() => setPanelCollapsed((p) => !p)}
              startNm={startNm}
              finishNm={finishNm}
            />
          </div>

          <div className="viewer-map">
            <CourseMap
              marks={effectiveMarks}
              selectedCards={selectedCourses}
              allCards={filteredCourses}
              focusedId={focusedId}
              onFocus={handleFocus}
              startNm={startNm}
              finishNm={finishNm}
              track={track}
            />
          </div>
        </div>
      )}
      </div>
    </>
  );
}
